import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Grid,
  Button,
  TextField,
  Alert,
  Paper
} from '@mui/material';
import { Business, GroupAdd, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios'; // Assuming global axios instance
import { UserRole } from '../../api/user';

const OnboardingPage: React.FC = () => {
  const { user, refreshToken } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<'SELECTION' | 'CREATE_TENANT' | 'JOIN_EXISTING'>('SELECTION');
  const [tenantName, setTenantName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Create Tenant
      // We assume the backend allows a user with no tenant to create one
      // The backend 'onboard' endpoint handles tenant creation + user role update
      const response = await api.post('/tenants/onboard', { name: tenantName, address: 'Default Address', contactEmail: user?.email, contactPhone: '0000000000' });  
      
      // If successful, we need to refresh the user's token/profile to get the new Role and TenantId
      // The backend 'create tenant' might return the updated user, or we just refresh token
      
      // Wait a moment for consistency if needed, then refresh
      await refreshToken();
      
      // Redirect to dashboard
      navigate('/');
    } catch (err: any) {
      console.error('Failed to create tenant:', err);
      setError(err.response?.data?.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Welcome, {user?.name}!
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Let's get you set up. Do you want to join an existing organization or create a new one?
        </Typography>
      </Box>

      {view === 'SELECTION' && (
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%', border: '1px solid #e0e0e0', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } }}>
              <CardActionArea onClick={() => setView('JOIN_EXISTING')} sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <GroupAdd sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" component="div" gutterBottom>
                  Join Existing Organization
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  My team already uses SmartFarm. I want to join them.
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ height: '100%', border: '1px solid #e0e0e0', transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 } }}>
              <CardActionArea onClick={() => setView('CREATE_TENANT')} sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Business sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h5" component="div" gutterBottom>
                  Create New Organization
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  I am starting a new SmartFarm workspace for my team.
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      )}

      {view === 'JOIN_EXISTING' && (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Join an Existing Organization
          </Typography>
          <Alert severity="info" sx={{ mt: 2, mb: 4, textAlign: 'left' }}>
            To join an existing organization, you must be invited by an Administrator.
          </Alert>
          <Typography variant="body1" paragraph>
            Please contact your SmartFarm Administrator and ask them to add your email address (<strong>{user?.email}</strong>) to the organization.
          </Typography>
          <Typography variant="body1" paragraph>
            Once they have added you, simply refresh this page or log in again to access the dashboard.
          </Typography>
          <Button variant="outlined" onClick={() => setView('SELECTION')} sx={{ mt: 2 }}>
            Back to Selection
          </Button>
        </Paper>
      )}

      {view === 'CREATE_TENANT' && (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h5" gutterBottom align="center">
            Create New Organization
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            You will be the Administrator of this new organization.
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleCreateTenant}>
            <TextField
              fullWidth
              label="Organization / Farm Name"
              variant="outlined"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button variant="outlined" onClick={() => setView('SELECTION')}>
                Back
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                size="large"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Organization'}
              </Button>
            </Box>
          </form>
        </Paper>
      )}
    </Container>
  );
};

export default OnboardingPage;
